/* eslint-disable camelcase */
import React from "react";
import DataTable from "react-data-table-component";
import PropTypes from "prop-types";
import { Spinner } from "reactstrap";
import styled from "styled-components";

const ProgressContainer = styled.div`
  text-align: center;
  padding-left: 0.5rem;
  padding-right: 0.5rem;
`;

const StyledDataTable = styled(DataTable)`
  .rdt_TableCol_Sortable {
    justify-content: space-between;

    &:first-child {
      font-weight: 400;
      letter-spacing: 0.388px;
      text-transform: uppercase;
      color: ${({ theme }) => theme.colors.tableColumnText};
    }
  }

  .rdt_TableHeadRow {
    border-bottom: none;
  }

  .rdt_TableRow {
    background: ${({ theme }) => theme.colors.tableBodyBackground};

    &:hover {
      background: ${({ theme }) => theme.colors.tableRowHoverBackground};
      border-bottom-color: rgba(0, 0, 0, 0.12);
      border-top: 1px solid rgba(0, 0, 0, 0.12);

      .rdt_TableCell[data-column-id="highlight"] {
        background-color: ${({ theme }) => theme.colors.tableRowHighlightBlue};
      }
    }

    .rdt_TableCell[data-column-id="highlight"] {
      padding: 0;
    }

    :has(.font-weight-bold) {
      background: ${({ theme }) => theme.colors.white};
    }

    .rdt_TableCell {
      .font-weight-bold {
        font-weight: bold;
      }
    }
  }
`;

const customStyles = {
  noData: {
    style: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: "1rem 0rem",
    },
  },
};

const CustomDataTable = ({
  data = [],
  columns = [],
  fixedHeader,
  noHeader,
  pagination = true,
  progressPending = false,
  paginationPerPage = 10,
  onChangePage,
  onChangeRowsPerPage,
  paginationServer,
  paginationTotalRows,
  subHeader,
  subHeaderComponent,
  progressComponent,
  defaultSortAsc,
  defaultSortField,
  onSort,
  sortServer,
  height = "auto",
  onRowClicked,
  theme,
  paginationRowsPerPageOptions = [10, 25, 50, 100],
  ...rest
}) => {
  const dataTableOptions = {
    customStyles,
    data,
    columns,
    pagination: true,
    highlightOnHover: true,
    persistTableHead: true,
    paginationRowsPerPageOptions,
    progressComponent: progressComponent || (
      <ProgressContainer>
        <Spinner
          animation="border"
          color="dark"
          size="sm"
        />
      </ProgressContainer>
    ),
    ...rest,
  };

  if (fixedHeader !== undefined) {
    dataTableOptions.fixedHeader = fixedHeader;
  }
  if (noHeader !== undefined) dataTableOptions.noHeader = noHeader;
  if (pagination !== undefined) dataTableOptions.pagination = pagination;

  if (progressPending !== undefined) {
    dataTableOptions.progressPending = progressPending;
  }

  if (paginationPerPage !== undefined) {
    dataTableOptions.paginationPerPage = paginationPerPage;
  }

  if (onChangePage !== undefined) {
    dataTableOptions.onChangePage = onChangePage;
  }

  if (onChangeRowsPerPage !== undefined) {
    dataTableOptions.onChangeRowsPerPage = onChangeRowsPerPage;
  }

  if (paginationServer !== undefined) {
    dataTableOptions.paginationServer = paginationServer;
  }

  if (paginationTotalRows !== undefined) {
    dataTableOptions.paginationTotalRows = paginationTotalRows;
  }
  if (subHeader !== undefined) dataTableOptions.subHeader = subHeader;

  if (subHeaderComponent !== undefined) {
    dataTableOptions.subHeaderComponent = subHeaderComponent;
  }
  if (onSort !== undefined) dataTableOptions.onSort = onSort;
  if (sortServer !== undefined) dataTableOptions.sortServer = sortServer;

  if (defaultSortField !== undefined) {
    dataTableOptions.defaultSortField = defaultSortField;
  }

  if (defaultSortAsc !== undefined) {
    dataTableOptions.defaultSortAsc =
      `${defaultSortAsc}`?.toLowerCase() === "asc";
  }

  return (
    <StyledDataTable
      {...dataTableOptions}
      fixedHeader
      fixedHeaderScrollHeight={height}
      onRowClicked={onRowClicked}
      theme={theme}
    />
  );
};

CustomDataTable.propTypes = {
  onChangePage: PropTypes.oneOfType([PropTypes.func]),
  fixedHeader: PropTypes.bool,
  noHeader: PropTypes.bool,
  pagination: PropTypes.bool,
  progressPending: PropTypes.bool,
  paginationPerPage: PropTypes.number,
  onChangeRowsPerPage: PropTypes.func,
  paginationServer: PropTypes.oneOfType([PropTypes.bool, PropTypes.object]),
  paginationTotalRows: PropTypes.number,
  subHeader: PropTypes.bool,
  subHeaderComponent: PropTypes.oneOfType([PropTypes.element]),
  progressComponent: PropTypes.element,
  defaultSortAsc: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  defaultSortField: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  onSort: PropTypes.func,
  sortServer: PropTypes.bool,
  height: PropTypes.string,
  onRowClicked: PropTypes.func,
  theme: PropTypes.string,
};

export default CustomDataTable;
